import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { MessageCircle, ArrowRight, Receipt, Shield, Smartphone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '../../i18n';

const COUNTRY_CODES = [
  { code: '55', country: 'BR', flag: '🇧🇷', format: '(XX) XXXXX-XXXX', placeholder: '(11) 99999-9999' },
  { code: '44', country: 'UK', flag: '🇬🇧', format: 'XXXX XXXXXX', placeholder: '7911 123456' },
  { code: '1', country: 'US', flag: '🇺🇸', format: '(XXX) XXX-XXXX', placeholder: '(555) 123-4567' },
  { code: '351', country: 'PT', flag: '🇵🇹', format: 'XXX XXX XXX', placeholder: '912 345 678' },
];

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { requestMagicLink, isRequestingMagicLink, requestMagicLinkError } = useAuth();
  const { isAuthenticated } = useAuthStore();
  const { isPortuguese } = useTranslation();

  // Default country based on locale
  const defaultCountry = COUNTRY_CODES.find(c => c.country === (isPortuguese ? 'BR' : 'UK')) || COUNTRY_CODES[0];

  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const formatPhone = (value: string, countryCode: string) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');

    // Format based on country
    if (countryCode === '55') {
      // Brazil: (XX) XXXXX-XXXX
      if (digits.length <= 2) return digits;
      if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    } else if (countryCode === '44') {
      // UK: XXXX XXXXXX
      if (digits.length <= 4) return digits;
      if (digits.length <= 10) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
      return `${digits.slice(0, 4)} ${digits.slice(4, 10)}`;
    } else if (countryCode === '1') {
      // US: (XXX) XXX-XXXX
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (countryCode === '351') {
      // Portugal: XXX XXX XXX
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
    }

    return digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value, selectedCountry.code);
    setPhone(formatted);
    setError(null);
  };

  const handleCountrySelect = (country: typeof COUNTRY_CODES[0]) => {
    setSelectedCountry(country);
    setShowCountryPicker(false);
    setPhone(''); // Reset phone when changing country
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Extract digits
    const digits = phone.replace(/\D/g, '');

    // Validate based on country
    const minLength = selectedCountry.code === '55' ? 10 : selectedCountry.code === '44' ? 10 : 10;
    const maxLength = selectedCountry.code === '55' ? 11 : selectedCountry.code === '44' ? 10 : 10;

    if (digits.length < minLength || digits.length > maxLength) {
      setError(isPortuguese
        ? 'Por favor, insira um numero de telefone valido'
        : 'Please enter a valid phone number');
      return;
    }

    // Add country code
    const fullPhone = `${selectedCountry.code}${digits}`;

    try {
      await requestMagicLink(fullPhone);
      setSuccess(true);
      // Navigate to verify page with phone
      setTimeout(() => {
        navigate('/auth/verify', { state: { phone: fullPhone } });
      }, 2000);
    } catch {
      setError(
        requestMagicLinkError?.message ||
          (isPortuguese ? 'Erro ao enviar link. Tente novamente.' : 'Error sending link. Please try again.')
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-secondary-900 dark:to-secondary-800 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="KeepNota" className="h-10 w-auto" />
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            KeepNota
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl">
            <CardContent>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
                {isPortuguese ? 'Entrar no KeepNota' : 'Sign in to KeepNota'}
              </h1>
              <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                {isPortuguese
                  ? 'Receba um link magico no seu WhatsApp para acessar sua conta'
                  : 'Receive a magic link on WhatsApp to access your account'}
              </p>

              {success ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {isPortuguese ? 'Link enviado!' : 'Link sent!'}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {isPortuguese
                      ? 'Verifique seu WhatsApp e clique no link para entrar.'
                      : 'Check your WhatsApp and click the link to sign in.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Phone input */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      {isPortuguese ? 'Numero de WhatsApp' : 'WhatsApp Number'}
                    </label>
                    <div className="relative flex">
                      {/* Country selector */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCountryPicker(!showCountryPicker)}
                          className="flex items-center gap-1 px-3 h-full bg-gray-100 dark:bg-secondary-700 border border-r-0 border-gray-300 dark:border-secondary-600 rounded-l-lg hover:bg-gray-200 dark:hover:bg-secondary-600 transition"
                        >
                          <span className="text-lg">{selectedCountry.flag}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-300">+{selectedCountry.code}</span>
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        </button>

                        {/* Dropdown */}
                        {showCountryPicker && (
                          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-600 rounded-lg shadow-lg z-50 min-w-[180px]">
                            {COUNTRY_CODES.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => handleCountrySelect(country)}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-secondary-700 transition ${
                                  selectedCountry.code === country.code ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                }`}
                              >
                                <span className="text-lg">{country.flag}</span>
                                <span className="text-sm text-gray-900 dark:text-white">+{country.code}</span>
                                <span className="text-xs text-gray-500">{country.country}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Phone input */}
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder={selectedCountry.placeholder}
                        className="input rounded-l-none flex-1"
                        autoComplete="tel"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}

                  {/* Submit button */}
                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={isRequestingMagicLink}
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    {isPortuguese ? 'Enviar link magico' : 'Send magic link'}
                  </Button>
                </form>
              )}

              {/* Info */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-secondary-700">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isPortuguese
                      ? 'Seu numero de telefone e usado apenas para autenticacao. Nao compartilhamos seus dados com terceiros.'
                      : 'Your phone number is only used for authentication. We do not share your data with third parties.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin link */}
          <p className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400">
            {isPortuguese ? 'Administrador?' : 'Administrator?'}{' '}
            <a
              href="/admin/login"
              className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              {isPortuguese ? 'Acesse aqui' : 'Access here'}
            </a>
          </p>
        </div>
      </main>

      {/* Features */}
      <footer className="p-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-secondary-800/50 rounded-xl">
            <MessageCircle className="w-8 h-8 text-green-600" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {isPortuguese ? 'Via WhatsApp' : 'Via WhatsApp'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isPortuguese ? 'Envie fotos dos recibos' : 'Send receipt photos'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-secondary-800/50 rounded-xl">
            <Receipt className="w-8 h-8 text-primary-600" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {isPortuguese ? 'OCR Automatico' : 'Auto OCR'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isPortuguese ? 'Extracao inteligente' : 'Smart extraction'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-secondary-800/50 rounded-xl">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {isPortuguese ? '100% Seguro' : '100% Secure'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isPortuguese ? 'Dados criptografados' : 'Encrypted data'}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthPage;
